import { HttpClient, HttpHeaders } from "@angular/common/http";
import { GlobalsService } from "./globals";
import { Injectable } from "@angular/core";
import { MCause } from "../models/cause.model";
import { map } from "rxjs/operators";
import { AnalisisMethod } from "../interfaces/treatment";
import { MCauseArray } from "../models/cause-array.model";
import { MTracingItem } from "../models/tracing-item-model";
import { MToolboxDashboard } from "../models/ToolboxDashboard.model";
import { MReportType } from "../models/report-type.model";
import { MOrganizationUser } from "../models/organization-user.model";
import { Observable } from "rxjs";
import { MResponsible } from "../models/responsible.model";

@Injectable({
  providedIn: "root",
})
export class SieniService {
  public toolbox: MToolboxDashboard = null;
  public orgUsers: MOrganizationUser[] = null;

  constructor(private http: HttpClient, private globals: GlobalsService) {}

  private header(): HttpHeaders {
    return new HttpHeaders({
      Authorization: "Bearer " + this.globals.user.token,
      Accept: "application/json",
      "Content-Type": "application/json",
    });
  }

  public setToolbox(newTbox: MToolboxDashboard) {
    this.toolbox = newTbox;
  }

  /**
   * @author Carlos Albornoz
   * @name setNonConformityCauses
   * @description Guarda las causas que pueda tener una no conformidad
   */
  public setNonConformityDefinitiveCauses(
    nonConformityId: number,
    causes: MCause[]
  ) {
    let body = {
      causas: [],
    };
    causes.forEach((c) => {
      body.causas.push({
        id: c.definitiveId,
        descripcion: c.description,
        metodo_analisis_id: c.method.id,
        seleccionado: c.definitiveSelected,
      });
    });
    return this.http
      .post(
        this.globals.apiUrl + "/api/gnc/" + nonConformityId + "/causas",
        body
      )
      .pipe(
        map((res: any) =>
          res.data.map((cause) => new MCause({ regular: cause }))
        )
      );
  }
  /**
   * @author Carlos Albornoz
   * @name getNonConformityDefinitiveCauses
   * @description Guarda las causas definitivas que pueda tener una no conformidad
   */
  public getNonConformityDefinitiveCauses(nonConformityId: number) {
    return this.http
      .get(this.globals.apiUrl + "/api/gnc/" + nonConformityId + "/causas")
      .pipe(
        map((res: any) =>
          res.data.map((cause) => new MCause({ regular: cause }))
        )
      );
  }

  /**
   * @author Carlos Albornoz
   * @name setAllNonConformityCausesByMethod
   * @description Obtiene todas las causas asociadas a una no conformidad
   * @param methods Este parametro permite filtrar las causas segun los metodos deseados
   */
  public setAllNonConformityCausesByMethod(
    methodId: number,
    nonConformityId: number,
    causes: MCauseArray[]
  ) {
    let anidated: boolean = false;
    if (methodId == 1 || methodId == 2) anidated = true;
    let body = {
      causas: [],
    };
    let method: AnalisisMethod = {
      id: methodId,
      nombre: causes[0].causes[0].method.name,
    };

    causes.forEach((cause) => {
      let causeTree = {
        causas_anidadas: anidated,
        causa: [],
      };
      cause.causes.forEach((item) => {
        let c = {
          id: item.id,
          texto: item.description,
          elegido: item.selected,
          metodo_abierto: methodId == 5 ? item.method.name : null,
          evidencias: methodId == 5 && item.evidence.length > 0 ? [] : null,
          dato:
            methodId == 3
              ? JSON.stringify({
                  magnitude: item.magnitude,
                  data: {
                    title: item.diagramData.title,
                    magnitude: item.diagramData.magnitude,
                    xAxys: item.diagramData.xAxys,
                    base64: item.diagramData.base64,
                  },
                })
              : null,
        };
        if (methodId == 5 && item.evidence.length > 0) {
          item.evidence.forEach((evidence) => {
            c.evidencias.push({
              id: evidence.id ? evidence.id : null,
              name: evidence.name,
              base64: evidence.base64,
            });
          });
        }
        causeTree.causa.push(c);
      });
      body.causas.push(causeTree);
    });
    console.log("REQ", body);

    return this.http
      .post(
        this.globals.apiUrl +
          "/api/metodo-analisis/" +
          methodId +
          "/no-conformidad/" +
          nonConformityId +
          "/causas",
        body
      )
      .pipe(
        map((res: any) =>
          res.data.map(
            (cause) =>
              new MCause(
                { byMethod: cause },
                methodId !== 5
                  ? method
                  : {
                      id: 5,
                      nombre: cause.metodo_abierto,
                    }
              )
          )
        )
      );
  }

  /**
   * @author Carlos Albornoz
   * @name getAllNonConformityCauses
   * @description Obtiene todas las causas asociadas a una no conformidad
   * @param methods Este parametro permite filtrar las causas segun los metodos deseados
   */
  public getAllNonConformityCauses(nonConformityId: number, methods?: string) {
    if (!methods) {
      methods = JSON.stringify({
        ishikawa: 1,
        five: 2,
        pareto: 3,
        lluvia: 4,
        others: 5,
      });
    }
    return this.http
      .get(
        this.globals.apiUrl +
          "/api/metodo-analisis/no-conformidad/" +
          nonConformityId +
          "/causas?metodo=" +
          methods
      )
      .pipe(
        map((res: any) => {
          let data = {
            ishikawa: [],
            five: [],
            pareto: [],
            rain: [],
            others: [],
          };
          data.five = res.data.five
            ? res.data.five.map(
                (cause) =>
                  new MCause(
                    { byMethod: cause },
                    {
                      id: 2,
                      nombre: "treatment.methods.because.title",
                    }
                  )
              )
            : null;
          data.ishikawa = res.data.ishikawa
            ? res.data.ishikawa.map(
                (cause) =>
                  new MCause(
                    { byMethod: cause },
                    {
                      id: 1,
                      nombre: "treatment.methods.ishikawa.title",
                    }
                  )
              )
            : null;
          data.pareto = res.data.pareto
            ? res.data.pareto.map(
                (cause) =>
                  new MCause(
                    { byMethod: cause },
                    {
                      id: 3,
                      nombre: "treatment.methods.pareto.title",
                    }
                  )
              )
            : null;
          data.rain = res.data.lluvia
            ? res.data.lluvia.map(
                (cause) =>
                  new MCause(
                    { byMethod: cause },
                    {
                      id: 4,
                      nombre: "treatment.methods.rain.title",
                    }
                  )
              )
            : null;
          data.others = res.data.others
            ? res.data.others.map(
                (cause) =>
                  new MCause(
                    { byMethod: cause },
                    {
                      id: 5,
                      nombre: cause.metodo_abierto,
                    }
                  )
              )
            : null;
          return data;
        })
      );
  }

  /**
   * @author Carlos Albornoz
   * @name deleteAllNonConformityCausesByMethod
   * @description Eliminas todas las causas asociadas aun metodo de analisis
   * @param conConformityId Id de la no conformidad a la que se le desean eliminar las causas
   * @param methodId Id del metodo de analisis al que se le desea eliminar las causas
   */
  public deleteAllNonConformityCausesByMethod(
    nonConformityId: number,
    methodId: number
  ) {
    return this.http.delete(
      this.globals.apiUrl +
        "/api/metodo-analisis/" +
        methodId +
        "/no-conformidad/" +
        nonConformityId +
        "/causas"
    );
  }

  /**
   * @author Carlos Albornoz
   * @name getFollowing
   * @description Trae la informacion de seguimiento de las no conformidades registradas
   */
  public getFollowing() {
    return this.http
      .get(`${this.globals.apiUrl}/api/gnc/seguimiento`, {
        headers: this.header(),
      })
      .pipe(map((res: any) => res.data.map((item) => new MTracingItem(item))));
  }

  public getHelp(url: string) {
    return this.http.post(
      `${this.globals.apiUrl}/api/ayuda-sistema/search/${this.toolbox.id}`,
      { url: url }
    );
  }

  public getHistory() {
    return this.http
      .get(`${this.globals.apiUrl}/api/gnc/historico`, {
        headers: this.header(),
      })
      .pipe(map((res: any) => res.data.map((item) => new MTracingItem(item))));
  }

  public sendToHistoryById(id: number) {
    return this.http.post(
      `${this.globals.apiUrl}/api/gnc/${id}/pasar-historico`,
      {},
      { headers: this.header() }
    );
  }

  public getReportTypes(idToolbox: string | number = 6) {
    return this.http
      .get(`${this.globals.apiUrl}/api/reporte/toolbox/${idToolbox}`, {
        headers: this.header(),
      })
      .pipe(map((res: any) => res.data.map((item) => new MReportType(item))));
  }

  /**
   * @author Carlos Albornoz
   * @name getReport
   * @description Retorne el reporte generado de una evaluacion segun su tipo
   */
  public getReport(url: string) {
    let header = new HttpHeaders({
      Authorization: "Bearer " + this.globals.user.token,
      Accept: "application/pdf",
      "Content-Type": "application/json",
    });
    return this.http.get(`${this.globals.apiUrl}/${url}`, {
      headers: header,
      responseType: "blob",
    });
  }

  /**
   * @author Carlos Albornoz
   * @name sendReminder
   * @description Envia un correo recordatorio al usuario encargado de una actividad de tratamiento
   */
  public sendReminder(nonConformityId: number, faseId: number, notes: string) {
    return this.http.post(
      `${this.globals.apiUrl}/api/recordatorio/gnc/${nonConformityId}/fase/${faseId}/actividad-ejecutar`,
      { observaciones: notes },
      { headers: this.header() }
    );
  }

  /**
   * @author Carlos Albornoz
   * @name sendToApproval
   * @description Envia una no conformidad a la fase de aprovación
   */
  // /api/gnc/{gncId}/aprobacion
  public sendToApproval(id: number) {
    return this.http.post(
      `${this.globals.apiUrl}/api/gnc/${id}/aprobacion`,
      {},
      { headers: this.header() }
    );
  }

  public getOrganizationUsers(): Observable<MOrganizationUser[]> {
    return this.http
      .get(`${this.globals.apiUrl}/api/organizacion/usuario`, {
        headers: this.header(),
      })
      .pipe(
        map((res: any) =>
          res.data.map((element) => new MOrganizationUser(element))
        )
      );
  }

  public async getCurrentOrganizationUsers() {
    if (this.orgUsers) {
      return await this.orgUsers;
    } else {
      let users: MOrganizationUser[] = [];
      const value = await this.getOrganizationUsers()
        .toPromise()
        .then(async (res) => {
          users = res;
          this.orgUsers = res;
        });
      return await users;
    }
  }

  public setResponsibles(id: number, responsibles): Observable<MResponsible[]> {
    let body = {
      responsables: responsibles,
    };

    return this.http
      .post(`${this.globals.apiUrl}/api/gnc/${id}/asignar-responsable`, body, {
        headers: this.header(),
      })
      .pipe(
        map((res: any) =>
          res.data.fases_nc.map((element) => new MResponsible(element))
        )
      );
  }
  public getSettings(parent: string, key: string) {
    return this.http.get(
      `${this.globals.apiUrl}/api/settings/${parent}/${key}`
    );
  }
}
